// Story 11.5 — Pure-logic del status de validación. Aislado de Zustand para
// tests sin deps.

import type { ValidationProblem } from './editor-types';

export type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid';

export interface ValidationState {
  status: ValidationStatus;
  errorCount: number;
  warningCount: number;
  lastErrors: ValidationProblem[];
  lastRunAt: number | null;
}

export const initialValidationState: ValidationState = {
  status: 'idle',
  errorCount: 0,
  warningCount: 0,
  lastErrors: [],
  lastRunAt: null,
};

/** Marca el inicio de un run; preserva últimos errores hasta el setResult. */
export function startValidation(state: ValidationState, now: number): ValidationState {
  return { ...state, status: 'validating', lastRunAt: now };
}

/** Aplica el resultado de un run (save/deploy) y mueve a `valid` o `invalid`.
 *  El parámetro `_state` se mantiene en la firma por simetría con
 *  `startValidation` (ambos son reducers puros) aunque la nueva entrada
 *  reemplaza por completo el estado anterior. */
export function applyResult(
  _state: ValidationState,
  errors: ValidationProblem[],
  warnings: ValidationProblem[] = [],
  now: number,
): ValidationState {
  const errorCount = errors.length;
  const warningCount = warnings.length;
  const status: ValidationStatus = errorCount > 0 ? 'invalid' : 'valid';
  return {
    status,
    errorCount,
    warningCount,
    lastErrors: errors,
    lastRunAt: now,
  };
}

/**
 * Decide si el indicador `valid` debe haber expirado (auto-fade tras 3s).
 * El componente UI llama `shouldFadeValid(state, Date.now())` periódicamente.
 */
export const VALID_FADE_MS = 3000;

export function shouldFadeValid(state: ValidationState, now: number): boolean {
  if (state.status !== 'valid') return false;
  if (state.lastRunAt == null) return false;
  return now - state.lastRunAt >= VALID_FADE_MS;
}
